import productService from '../../../../services/products';
import geometry from '../../../../scene/util/geometry';
import tacHelpers from '../../tacHelpers';
import constants from '../../../../settings/constants';
import { config as asConfig } from '../../../../scene/boaxel/AdjustableConfig';

import {
  getDependencyDiff,
  getDependentItems,
  getSlotSources,
} from './dependentItems';
import { causedBracketChanges } from './brackets';
import superSection from './supersection';
import { getCuttableMountingRailData } from './mountingRail';
import { unique } from '../../../../util/array';
import { ITEMS } from '../../../../constants';

const BRACKET_HEIGHT = 80;

function hasBlockingTable(slot, sections, tac) {
  return sections.some(section => {
    if (
      tacHelpers.hasTable(section) &&
      geometry.collides(section, slot, { bottom: 1000 })
    ) {
      const table = section.items.find(item =>
        productService.isType(item, ITEMS.TABLE)
      );
      const globalTable = tacHelpers.getGlobalCoords(table, tac);
      return globalTable.y > slot.y;
    }
    return false;
  });
}

function hasBlockingInsert(slot, sections, tac) {
  return sections.some(section => {
    if (
      !productService.fitsTable(section) &&
      geometry.collides(section, slot)
    ) {
      return section.items.some(item => {
        const gItem = tacHelpers.getGlobalCoords(item, tac);
        return gItem.y < slot.y;
      });
    }
    return false;
  });
}

function validTableHeight(slot) {
  return slot.y >= asConfig.legs.minHeight && slot.y <= asConfig.legs.maxHeight;
}

function slotFitsSection(slot, section) {
  if (section.width === slot.width) {
    return true;
  }
  if (section.logic.extendable) {
    return (
      section.width >= asConfig.section.minWidth &&
      section.width <= asConfig.section.maxWidth &&
      slot.width >= asConfig.section.minWidth &&
      slot.width <= asConfig.section.maxWidth
    );
  }
  return false;
}

function filterSlots(slots, tac) {
  const sections = tacHelpers.getSections(tac);
  slots = slots
    .filter(slot => {
      if (productService.isInsert(slot)) {
        return sections.some(section => {
          // if the slot fits inside a section, it's ok
          // padding since sections are between uprights, and stuff that goes in sections extend a bit over them
          // and remove top and bottom since we don't want to allow stuff positioned above right rail
          const padding = geometry.uniformPadding(
            productService.getPostWidth()
          );
          padding.top = 0;
          padding.bottom = 0;
          return (
            geometry.contains(
              section,
              {
                ...slot,
                height: productService.isTable(slot)
                  ? slot.height + BRACKET_HEIGHT
                  : BRACKET_HEIGHT, // to not filter out items that stretches outside
                y: slot.y + slot.height - BRACKET_HEIGHT,
                x: slot.x + productService.getPostWidth(),
              },
              padding
            ) && slotFitsSection(slot, section)
          );
        });
      }
      return true;
      // since lagkapten sections are at the same spot as other sections,
      // multiple sections share the same upright, thus yielding duplicate slots.
    })
    .filter(unique);

  slots
    .filter(slot => slot.logic.extendable)
    .forEach(slot => {
      const parentid = slot.parent.itemid;
      const section = sections
        .filter(
          section =>
            productService.isExtendable(section) &&
            geometry.contains(section, slot)
        )
        .find(
          section =>
            section.uprights.find(upright => upright.itemid === parentid) ||
            section.items.find(item => item.itemid === parentid)
        );

      slot.width = section ? section.width : slot.parent.width;
    });

  slots = slots.filter(slot => {
    if (productService.isType(slot, ITEMS.TABLE)) {
      return validTableHeight(slot) && !hasBlockingInsert(slot, sections, tac);
    }
    return !hasBlockingTable(slot, sections, tac);
  });

  return slots;
}

function getProppingItemsToAdapt(tac, movingItem, previous, original) {
  return tacHelpers.getClothesRails(tac).filter(cr => {
    if (!movingItem) {
      return true;
    }
    const globalCr = tacHelpers.getGlobalCoords(cr, tac);
    const padding = {
      top: constants.ROOM_MAX.height,
      bottom: constants.ROOM_MAX.height,
      front: constants.ROOM_DEPTH,
      back: constants.ROOM_DEPTH,
    };
    return [movingItem, previous, original].some(
      item => item && geometry.collides(globalCr, item, padding)
    );
  });
}

function filterVariants(tac, variants) {
  const sections = tacHelpers
    .getAllItems(tac.items)
    .filter(productService.isSection);
  return variants.filter(variant => {
    if (
      productService.isShelf(variant) &&
      productService.isExtendable(variant)
    ) {
      return sections.some(productService.isExtendable);
    }
    return true;
  });
}

/**
 * Filters out those of an item's variants for which there is no section of
 * suitable width in the TAC. This is only a prescreening and it does not
 * consider other aspects such as whether the sections are full.
 * (If the variants are not inserts, they will not be filtered out regardless
 * of their width.)
 * @param {Array} variants The variants to be filtered.
 * @param {object} tac The TAC.
 * @returns {Array} The remaining variants that were not filtered out.
 */
function preScreenSlots(variants, tac) {
  const availableSectionWidths = tacHelpers
    .getSections(tac)
    .map(section => section.width)
    .filter(unique);

  return variants.filter(
    variant =>
      !productService.isInsert(variant) ||
      availableSectionWidths.find(
        width => Math.abs(variant.width - width) <= 100
      )
  );
}

const api = {
  filterSlots,
  filterVariants,
  getDependencyDiff,
  getDependentItems,
  getSlotSources,
  causedBracketChanges,
  getProppingItemsToAdapt,
  superSection,
  preScreenSlots,
  getCuttableMountingRailData,
};

Object.assign(api, superSection);

export default api;
